import { useQuery } from '@tanstack/react-query';

import { endpoints, type ProductPerformanceQuery, type ProfitabilityQuery, type SalesSeriesQuery, type SettlementQuery } from './api';

/**
 * Settlement reconciliation hooks.
 *
 * Settlements come from the channel finance sync, so the figures are empty until
 * an account has synced (or the demo seed has run).
 *
 * Both reads take the same period, and the screen passes the same one to both:
 * the categories a seller opens are the categories the lines below add up to,
 * and they only do so while the two cover the same movements (EPZ-0072).
 */

export function useSettlementSummary(query: SettlementQuery) {
    return useQuery({
        queryKey: ['settlement-summary', query],
        queryFn: () => endpoints.settlementSummary(query),
        placeholderData: (previous) => previous,
    });
}

export function useSettlements(query: SettlementQuery) {
    return useQuery({
        queryKey: ['settlements', query],
        queryFn: () => endpoints.settlements(query),
        placeholderData: (previous) => previous,
    });
}

/** Where a link into Kârlılık lands: on the rows that lost money (EPZ-0222). */
export interface ProfitabilitySearch {
    zarar?: true;
}

/**
 * The route's reading of its address. The notice that products were sold at a
 * loss links to `/karlilik?zarar=1`, which the router has already read as the
 * number 1; anything else the address says is dropped.
 */
export function validateProfitabilitySearch(search: Record<string, unknown>): ProfitabilitySearch {
    return search.zarar === 1 || search.zarar === '1' || search.zarar === true ? { zarar: true } : {};
}

/**
 * What each order, product or connection earned over a period (EPZ-0170).
 *
 * The figures and the totals come whole from the API, which works them out in
 * kuruş; nothing here adds them up again, so a row and its total cannot drift
 * apart on the screen.
 */
export function useProfitability(query: ProfitabilityQuery) {
    return useQuery({
        queryKey: ['profitability', query],
        queryFn: () => endpoints.profitability(query),
        placeholderData: (previous) => previous,
    });
}

/**
 * The sales trend: orders, units and revenue per day, week or month, under
 * Finans beside the settlements (EPZ-0226). The previous answer stays on screen
 * while a changed filter is read, so the chart does not blink empty.
 */
export function useSalesSeries(query: SalesSeriesQuery) {
    return useQuery({
        queryKey: ['sales-series', query],
        queryFn: () => endpoints.salesSeries(query),
        placeholderData: (previous) => previous,
    });
}

/**
 * Which variants bring in the period's revenue, by ABC class, and which held
 * stock has not sold (EPZ-0227). The classes, shares and counts come from the
 * API, so the chips and the rows below them cannot disagree.
 */
export function useProductPerformance(query: ProductPerformanceQuery) {
    return useQuery({
        queryKey: ['product-performance', query],
        queryFn: () => endpoints.productPerformance(query),
        placeholderData: (previous) => previous,
    });
}

/**
 * What the sales trend reads from its address: the product it is narrowed to,
 * so a product's page can open the trend on that product.
 */
export interface SalesTrendSearch {
    urun?: string;
}

/** The route's reading of its address: anything it does not know is dropped. */
export function validateSalesTrendSearch(search: Record<string, unknown>): SalesTrendSearch {
    return typeof search.urun === 'string' && search.urun !== '' ? { urun: search.urun } : {};
}
